// @flow
import moment from 'moment/moment';
import { FormattedMessage } from 'react-intl';
import { getMemberKey, getMemberName } from '../AffordaHousehold/utils';
import {
  BUTTON_GROUP_SX,
  COMMON_PAY_FREQUENCY_OPTIONS,
  PAY_FREQUENCY,
} from './constants';
import { validateNoSpecialChars } from 'utils/validateNoSpecialChars';

export const WAGES_AND_INCOME_COMMON_FIELD_NAMES = {
  id: 'id',
  memberId: 'memberId',
  source: 'source',
  payFrequency: 'payFrequency',
  endDate: 'endDate',
  weeklyAmount: 'weeklyAmount',
  monthlyAmount: 'monthlyAmount',
  annualAmount: 'annualAmount',
  claimNumber: 'claimNumber',
  startDate: 'startDate',

  streetAddress: 'streetAddress',
  city: 'city',
  state: 'state',
  zipCode: 'zipCode',

  isOpen: 'isOpen',
};

export const YES_NO_OPTIONS = [
  {
    label: <FormattedMessage id="app.yes" />,
    value: 'yes',
  },
  {
    label: <FormattedMessage id="app.no" />,
    value: 'no',
  },
];

export const GridProps = {
  xs: 12,
  sm: 6,
  md: 6,
};

export const FIELD_TEXT_REQUIRED = {
  variant: 'text',
  GridProps: {
    xs: 12,
  },
  rules: {
    required: true,
    validate: validateNoSpecialChars,
  },
};

export const FIELD_COUNTER_REQUIRED = {
  variant: 'counter',
  GridProps: {
    xs: 12,
  },
  rules: {
    required: true,
  },
};

export const FIELD_YES_NO = {
  variant: 'buttonGroup',
  rules: {
    required: true,
  },
  options: YES_NO_OPTIONS,
  direction: 'row',
  GridProps: {
    xs: 12,
  },
};

export const FIELD_AMOUNT = {
  variant: 'number',
  GridProps,
  rules: {
    max: 499999.99,
    min: 0,
    step: 0.01,
    required: true,
  },
  adornment: '$',
};

export const FIELD_DATE = {
  variant: 'date',
  GridProps: {
    xs: 6,
  },
};

export const FIELD_DATE_PAST = {
  ...FIELD_DATE,
  disableFuture: true,
  defaultCalendarMonth: moment(moment().year(), 'YYYY'),
  rules: {
    required: true,
    maxDate: moment().startOf('day'),
  },
};

export const FIELD_DATE_FUTURE = {
  ...FIELD_DATE,
  disablePast: true,
  defaultCalendarMonth: moment(moment().month(), 'mm'),
  rules: {
    required: true,
    minDate: moment().startOf('day'),
  },
};

export const FIELD_MEMBER_SELECTED = {
  watchFields: [WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId],
  hidden: ({ getValues }) =>
    !getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId)?.length,
};

export const FIELD_PAY_FREQUENCY = {
  watchFields: [
    WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId,
    WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency,
  ],
  hidden: ({ getValues }) =>
    !getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId)?.length ||
    !getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency)?.length,
};

export const FIELD_PAY_FREQUENCY_WEEKLY = {
  watchFields: [
    WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId,
    WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency,
  ],
  hidden: ({ getValues }) =>
    !getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId)?.length ||
    !getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency)?.length ||
    getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency) !==
      PAY_FREQUENCY.weekly,
};

export const FIELD_PAY_FREQUENCY_MONTHLY = {
  watchFields: [
    WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId,
    WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency,
  ],
  hidden: ({ getValues }) =>
    !getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId)?.length ||
    !getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency)?.length ||
    getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency) !==
      PAY_FREQUENCY.monthly,
};

export const FIELD_PAY_FREQUENCY_ANNUALLY = {
  watchFields: [
    WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId,
    WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency,
  ],
  hidden: ({ getValues }) =>
    !getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId)?.length ||
    !getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency)?.length ||
    getValues(WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency) !==
      PAY_FREQUENCY.annually,
};

export const FIELD_GROUP_BUTTONS = {
  variant: 'buttonGroup',
  direction: 'row',
  rules: {
    required: true,
  },
  GridProps: {
    xs: 12,
  },
  sx: BUTTON_GROUP_SX,
};

export const FIELD_SET_COMMON_PAY_FREQUENCY = [
  {
    ...FIELD_GROUP_BUTTONS,
    ...FIELD_MEMBER_SELECTED,
    label: (
      <FormattedMessage id="afforda.wagesAndIncome.details.payFrequency" />
    ),
    name: WAGES_AND_INCOME_COMMON_FIELD_NAMES.payFrequency,
    rules: {
      required: true,
    },
    options: Object.values(COMMON_PAY_FREQUENCY_OPTIONS)?.map((option) => ({
      value: option.value,
      label: <FormattedMessage id={option.text} />,
    })),
    GridProps: {
      xs: 12,
    },
    direction: 'row',
  },
  {
    ...FIELD_AMOUNT,
    ...FIELD_PAY_FREQUENCY_WEEKLY,
    label: (
      <FormattedMessage id="afforda.wagesAndIncome.details.weeklyAmount" />
    ),
    name: WAGES_AND_INCOME_COMMON_FIELD_NAMES.weeklyAmount,
    GridProps: {
      xs: 12,
    },
  },
  {
    ...FIELD_AMOUNT,
    ...FIELD_PAY_FREQUENCY_MONTHLY,
    label: (
      <FormattedMessage id="afforda.wagesAndIncome.details.monthlyAmount" />
    ),
    name: WAGES_AND_INCOME_COMMON_FIELD_NAMES.monthlyAmount,
    GridProps: {
      xs: 12,
    },
  },
  {
    ...FIELD_AMOUNT,
    ...FIELD_PAY_FREQUENCY_ANNUALLY,
    label: (
      <FormattedMessage id="afforda.wagesAndIncome.details.annualAmount" />
    ),
    name: WAGES_AND_INCOME_COMMON_FIELD_NAMES.annualAmount,
    GridProps: {
      xs: 12,
    },
  },
];

export const FIELD_MEMBERS = ({ members, disabledMembers }) =>
  (members?.length ?? 0) > 1
    ? [
        {
          name: WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId,
          variant: 'buttonGroup',
          watchFields: [WAGES_AND_INCOME_COMMON_FIELD_NAMES.memberId],
          rules: {
            required: true,
          },
          direction: 'row',
          GridProps: {
            xs: 12,
          },
          sx: BUTTON_GROUP_SX,
          options: members.map((member) => ({
            value: getMemberKey(member),
            label: getMemberName(member),
            disabled: disabledMembers?.includes(getMemberKey(member)),
          })),
        },
      ]
    : [];
